import {Show} from 'platform/foundation';

import {useContext, useRef, useState} from 'react';

import {isNil} from 'ramda';

import {useOnMount, type TestIdProps} from 'shared/web';

import {useHttpCalls} from '../../api/useHttpCalls';
import {useDataGridContext} from '../../context/useDataGridContext';
import {EagGridConfigReactContext} from '../../context/useEagGridConfig';
import {useActionsDefinition, useGridRows} from '../../store/selectors';
import {useGridBulkActions} from '../../store/selectors/primitives';
import {useEagGridStore} from '../../store/useEagGridStore';
import {createBulkOperationSelectionSource} from '../../utils/createBulkOperationSelectionSource';
import {BulkOperations} from '../BulkOperations/BulkOperations';
import {GridPagination, type PaginationProps} from '../Pagination/GridPagination';

type GridBulkOperationsProps = TestIdProps & {
  pagination?: PaginationProps;
  onRefresh: () => void;
};

function LoadBulkActions() {
  const store = useEagGridStore();
  const http = useHttpCalls();
  const mounted = useRef(true);
  useOnMount(
    () => {
      mounted.current = true;
      http.getBulkActions().then((discovery) => {
        if (mounted.current) {
          store.setBulkActions(discovery);
        }
      });
    },
    () => {
      mounted.current = false;
      store.setBulkActions(undefined);
    }
  );
  return null;
}

/** Stable native footer: discovery, selection, dialogs and pagination share one grid owner. */
export function GridBulkOperations(props: GridBulkOperationsProps) {
  const config = useContext(EagGridConfigReactContext);
  const gridContext = useDataGridContext();
  const store = useEagGridStore();
  const http = useHttpCalls();
  const [selectionSource] = useState(() => createBulkOperationSelectionSource(store));
  const actionsDefinition = useActionsDefinition();
  const rows = useGridRows();
  const discovery = useGridBulkActions();
  const actionCallback = gridContext?.bulkOperationActionCallback;
  const services =
    store.getState().disableAllActions || isNil(actionCallback)
      ? undefined
      : config?.bulkOperations;
  const gridCode = store.getState().gridCode;
  const presetInstanceId = store.getState().presetInstanceId ?? undefined;

  if (services === undefined) {
    return (
      <Show when={props.pagination}>{(pagination) => <GridPagination {...pagination} />}</Show>
    );
  }

  return (
    <>
      <LoadBulkActions />
      <BulkOperations
        gridCode={gridCode}
        presetInstanceId={presetInstanceId}
        discovery={discovery}
        services={services}
        actionCallback={actionCallback}
        selectionSource={selectionSource}
        entityResourceResolver={config?.entityResourceResolver}
        actionsDefinition={actionsDefinition ?? {}}
        onFilteredSelectionChange={(selected) => {
          if (selected) {
            store.selectAllFiltered();
          } else {
            store.selectAll(rows.map((row) => row.id));
          }
        }}
        pageRecordIds={rows.map((row) => row.id)}
        onSelectionChange={(recordIds) => store.setSelectedRowIds(new Set(recordIds))}
        submitFiltered={http.submitFilteredBulkOperation}
        onRefresh={() => {
          store.deselectAll();
          props.onRefresh();
        }}
        pagination={props.pagination}
        data-testid={props['data-testid']}
      />
    </>
  );
}
